import {
    Box,
    Center,
    Flex,
    Grid,
    GridItem,
    Skeleton,
    Stack,
    Tag,
    Text,
    Separator,
    Dialog,
    Portal,
    CloseButton
} from "@chakra-ui/react";

import { ArrowRightIconSM } from "@digital-services-gard-as/anchor-icons";
import { useTranslation } from "react-i18next";
import { useCallback } from "react";
import { Button } from "@digital-services-gard-as/anchor-react";
import { RelatedCompanyDto } from "@/api/usersApi";
import { TruncatedText } from "@/components/TruncatedText";

type BranchCompanyModalProps = {
    isOpen: boolean;
    onClose: () => void;
    selectedBranch: RelatedCompanyDto | null;
    setSelectedBranch: (branch: RelatedCompanyDto) => void;
    branchCompanies: RelatedCompanyDto[];
    isLoading: boolean;
    isError: boolean;
};

export const BranchCompanyModal = ({
    isOpen,
    onClose,
    selectedBranch,
    setSelectedBranch,
    branchCompanies = [],
    isLoading,
    isError
}: BranchCompanyModalProps) => {
    const { t } = useTranslation(["claimsInvoicesOverview", "main"]);

    const handleSelectBranchById = useCallback(
        (e: React.MouseEvent<HTMLButtonElement>) => {
            const crmId = e.currentTarget.dataset.companyId;
            if (!crmId) {
                return;
            }
            const branch = branchCompanies.find(c => String(c.crmId ?? "") === String(crmId));
            if (!branch) {
                return;
            }
            setSelectedBranch(branch);
            globalThis.sessionStorage.setItem("selectedBranch", JSON.stringify(branch));
            onClose();
        },
        [branchCompanies, onClose, setSelectedBranch]
    );

    const handleOpenChange = useCallback(
        (e: { open: boolean }) => {
            if (!e.open) {
                onClose();
            }
        },
        [onClose]
    );
    return (
        <Dialog.Root open={isOpen} size="lg" id="branchCompanyModal" onOpenChange={handleOpenChange}>
            <Portal>
                <Dialog.Backdrop p="1rem" />
                <Dialog.Positioner>
                    <Dialog.Content p="1rem" data-testid="branchCompanyModal">
                        <Dialog.Header textAlign="left">
                            <Dialog.Title>{t("branchCompanyModal.title")}</Dialog.Title>
                        </Dialog.Header>
                        <Dialog.CloseTrigger asChild id="branchCompanyModalClose">
                            <CloseButton size="sm" />
                        </Dialog.CloseTrigger>
                        <Dialog.Body gap="2rem" display="flex" flexDirection="column">
                            {isLoading && (
                                <Stack>
                                    <Skeleton height="20px" />
                                    <Skeleton height="20px" />
                                    <Skeleton height="20px" />
                                </Stack>
                            )}
                            {isError && (
                                <Center>
                                    <Text>{t("branchCompanyModal.noData")}</Text>
                                </Center>
                            )}
                            <Flex flexDir="column">
                                {branchCompanies.map((company, index) => (
                                    <Box key={company.crmId}>
                                        <Flex alignItems="center" justify="space-between">
                                            <Grid
                                                templateRows="repeat(1, 1fr)"
                                                templateColumns="repeat(2, 1fr)"
                                                gap={2}
                                            >
                                                <GridItem w="12rem">
                                                    <Text asChild>
                                                        <b>{t("branchCompanyModal.branchCompany")}</b>
                                                    </Text>
                                                    <Box whiteSpace="nowrap" overflow="hidden" textOverflow="ellipsis">
                                                        <TruncatedText text={company.companyName} />
                                                    </Box>
                                                </GridItem>
                                                <GridItem ml="2rem">
                                                    <Text asChild>
                                                        <b>{t("branchCompanyModal.branchCompanyCity")}</b>
                                                    </Text>
                                                    <Box maxW="12rem">
                                                        <TruncatedText text={company.billingCity} />
                                                    </Box>
                                                </GridItem>
                                            </Grid>
                                            <Flex>
                                                {selectedBranch?.crmId === company.crmId && (
                                                    <Tag.Root size="sm" variant="solid" colorPalette="blue">
                                                        {t("branchCompanyModal.selected")}
                                                    </Tag.Root>
                                                )}
                                            </Flex>
                                            <Flex>
                                                <Button
                                                    data-testid="selectBtn"
                                                    size="sm"
                                                    icon={<ArrowRightIconSM />}
                                                    variant="icon-only"
                                                    aria-label="Select company"
                                                    data-company-id={String(company.crmId ?? "")}
                                                    onClick={handleSelectBranchById}
                                                />
                                            </Flex>
                                        </Flex>
                                        {branchCompanies.length - 1 > index && <Separator />}
                                    </Box>
                                ))}
                                {branchCompanies.length === 0 && (
                                    <Center>
                                        <Text>{t("branchCompanyModal.noData")}</Text>
                                    </Center>
                                )}
                            </Flex>
                            <Button data-testid="cancelBtn" variant="tertiary" onClick={onClose}>
                                {t("main:cancel")}
                            </Button>
                        </Dialog.Body>
                    </Dialog.Content>
                </Dialog.Positioner>
            </Portal>
        </Dialog.Root>
    );
};
